import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  getProducts, deleteProduct, getArticles, deleteArticle,
  getSettings, updateSettings, getImageUrl, reorderProducts
} from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from "@/components/ui/dropdown-menu";
import ImageUploader from "@/components/ImageUploader";
import {
  Plus, MoreVertical, Pencil, Trash2, Eye, EyeOff,
  ArrowLeft, Package, BookOpen, Settings, GripVertical, Lock, LogOut
} from "lucide-react";
import GradientBackground from "@/components/GradientBackground";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "fallback";

function PasswordGate({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [shaking, setShaking] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem("admin_auth", "true");
      onSuccess();
    } else {
      setError(true);
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
      setPassword("");
    }
  };

  return (
    <div className="min-h-screen text-white relative overflow-hidden flex items-center justify-center" data-testid="admin-password-gate">
      <GradientBackground />
      <div className={`relative z-10 w-full max-w-sm mx-4 ${shaking ? 'animate-shake' : ''}`}>
        <div className="bg-black/60 backdrop-blur-xl border-2 border-[#ff0090]/40 rounded-3xl p-8 shadow-2xl shadow-[#ff0090]/20">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#ff0090]/20 border-2 border-[#ff0090] flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#ff0090]" />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-white">Admin-Bereich</h1>
            <p className="text-white/60 text-sm mt-2">Bitte Codewort eingeben</p>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(false);
                }}
                placeholder="Codewort..."
                className={`w-full bg-black/40 border-2 ${error ? 'border-red-500' : 'border-[#ff0090]/40'} rounded-xl text-white placeholder:text-white/40 focus:border-[#ff0090] focus:ring-[#ff0090]/20 h-12`}
                autoFocus
                data-testid="admin-password-input"
              />
              {error && (
                <p className="text-red-400 text-sm text-center">Falsches Codewort</p>
              )}
              <Button
                type="submit"
                className="w-full h-12 bg-[#ff0090] hover:bg-[#ff0090]/80 text-white font-semibold rounded-xl transition-all"
                data-testid="admin-password-submit"
              >
                Einloggen
              </Button>
            </div>
          </form>
          
          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-white/50 hover:text-white transition-colors">
              ← Zurück zur Startseite
            </Link>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-10px); }
          40%, 80% { transform: translateX(10px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
}

// Sortable Product Item Component
function SortableProductItem({ product, navigate, setDeleteDialog }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-4 p-4 border-b border-slate-100 last:border-0 bg-white transition-colors ${isDragging ? 'shadow-lg rounded-xl' : 'hover:bg-slate-50'}`}
      data-testid={`admin-product-${product.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded touch-none"
        data-testid={`drag-handle-${product.id}`}
      >
        <GripVertical className="w-4 h-4 text-slate-400" />
      </button>
      {product.image_url ? (
        <img src={getImageUrl(product.image_url)} alt={product.title} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
      ) : (
        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Package className="w-5 h-5 text-slate-400" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-slate-900 truncate">{product.title}</h3>
        <p className="text-sm text-slate-500 truncate">{product.description || 'Keine Beschreibung'}</p>
      </div>
      <Badge variant={product.is_published ? "default" : "secondary"} className={`flex-shrink-0 hidden sm:flex ${product.is_published ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-500'}`}>
        {product.is_published ? <><Eye className="w-3 h-3 mr-1" /> Live</> : <><EyeOff className="w-3 h-3 mr-1" /> Entwurf</>}
      </Badge>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="flex-shrink-0" data-testid={`product-menu-${product.id}`}>
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => navigate(`/admin/products/${product.id}`)} data-testid={`edit-product-${product.id}`}>
            <Pencil className="w-4 h-4 mr-2" /> Bearbeiten
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => window.open(`/product/${product.id}`, '_blank')} data-testid={`view-product-${product.id}`}>
            <Eye className="w-4 h-4 mr-2" /> Ansehen
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setDeleteDialog({ type: 'product', id: product.id, title: product.title })}
            className="text-red-600 focus:text-red-600"
            data-testid={`delete-product-${product.id}`}
          >
            <Trash2 className="w-4 h-4 mr-2" /> Löschen
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// Product List with Drag & Drop
function ProductList({ products, setProducts, navigate, setDeleteDialog }) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = products.findIndex((p) => p.id === active.id);
      const newIndex = products.findIndex((p) => p.id === over.id);

      const newProducts = arrayMove(products, oldIndex, newIndex);
      setProducts(newProducts);

      // Update order in backend
      try {
        const orderUpdate = newProducts.map((p, idx) => ({ id: p.id, order: idx }));
        await reorderProducts(orderUpdate);
        toast.success("Reihenfolge gespeichert");
      } catch (e) {
        toast.error("Fehler beim Speichern der Reihenfolge");
        // Revert on error
        setProducts(products);
      }
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="products-list">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2 text-sm text-slate-600">
        <GripVertical className="w-4 h-4" />
        <span>Ziehe die Produkte um die Reihenfolge zu ändern</span>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={products.map(p => p.id)} strategy={verticalListSortingStrategy}>
          {products.map((product) => (
            <SortableProductItem
              key={product.id}
              product={product}
              navigate={navigate}
              setDeleteDialog={setDeleteDialog}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(null);

  useEffect(() => {
    const auth = sessionStorage.getItem("admin_auth");
    if (auth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  const loadData = useCallback(() => {
    if (!isAuthenticated) return;
    setLoading(true);
    Promise.all([getProducts(), getArticles(), getSettings()])
      .then(([prods, arts, sett]) => {
        setProducts(prods);
        setArticles(arts);
        setSettings(sett);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [isAuthenticated]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDeleteProduct = async (id) => {
    try {
      await deleteProduct(id);
      setProducts(prev => prev.filter(p => p.id !== id));
      toast.success("Produkt gelöscht");
    } catch { toast.error("Fehler beim Löschen"); }
    setDeleteDialog(null);
  };

  const handleDeleteArticle = async (id) => {
    try {
      await deleteArticle(id);
      setArticles(prev => prev.filter(a => a.id !== id));
      toast.success("Artikel gelöscht");
    } catch { toast.error("Fehler beim Löschen"); }
    setDeleteDialog(null);
  };

  const handleSaveSettings = async () => {
    try {
      const updated = await updateSettings(settings);
      setSettings(updated);
      toast.success("Einstellungen gespeichert");
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_auth");
    setIsAuthenticated(false);
    toast.success("Erfolgreich ausgeloggt");
  };

  // Show password gate if not authenticated
  if (!isAuthenticated) {
    return <PasswordGate onSuccess={() => setIsAuthenticated(true)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors" data-testid="admin-back-link">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="font-['Outfit'] font-bold text-lg text-slate-900">Admin</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm font-medium text-indigo-500 hover:text-indigo-600" data-testid="admin-view-site">
              Seite ansehen
            </Link>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleLogout}
              className="text-slate-500 hover:text-red-600 hover:border-red-300"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-1" />
              Ausloggen
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Tabs defaultValue="products" data-testid="admin-tabs">
          <TabsList className="mb-8 bg-white border border-slate-200 p-1 rounded-xl" data-testid="admin-tabs-list">
            <TabsTrigger value="products" className="rounded-lg gap-2 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700" data-testid="tab-products">
              <Package className="w-4 h-4" /> Produkte
            </TabsTrigger>
            <TabsTrigger value="blog" className="rounded-lg gap-2 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700" data-testid="tab-blog">
              <BookOpen className="w-4 h-4" /> Blog
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg gap-2 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700" data-testid="tab-settings">
              <Settings className="w-4 h-4" /> Einstellungen
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products" data-testid="products-tab-content">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900">Produkte</h2>
              <Button
                onClick={() => navigate('/admin/products/new')}
                className="btn-gradient rounded-full gap-2"
                data-testid="add-product-btn"
              >
                <Plus className="w-4 h-4" /> Neues Produkt
              </Button>
            </div>

            {products.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center" data-testid="products-empty">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">Noch keine Produkte</p>
                <Button onClick={() => navigate('/admin/products/new')} className="btn-gradient rounded-full gap-2" data-testid="add-first-product-btn">
                  <Plus className="w-4 h-4" /> Erstes Produkt erstellen
                </Button>
              </div>
            ) : (
              <ProductList 
                products={products} 
                setProducts={setProducts}
                navigate={navigate}
                setDeleteDialog={setDeleteDialog}
              />
            )}
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog" data-testid="blog-tab-content">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900">Blog-Artikel</h2>
              <Button
                onClick={() => navigate('/admin/blog/new')}
                className="btn-gradient rounded-full gap-2"
                data-testid="add-article-btn"
              >
                <Plus className="w-4 h-4" /> Neuer Artikel
              </Button>
            </div>

            {articles.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center" data-testid="articles-empty">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">Noch keine Artikel</p>
                <Button onClick={() => navigate('/admin/blog/new')} className="btn-gradient rounded-full gap-2" data-testid="add-first-article-btn">
                  <Plus className="w-4 h-4" /> Ersten Artikel schreiben
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="articles-list">
                {articles.map((article) => (
                  <div key={article.id} className="flex items-center gap-4 p-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors" data-testid={`admin-article-${article.id}`}>
                    {article.cover_image_url ? (
                      <img src={getImageUrl(article.cover_image_url)} alt={article.title} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-5 h-5 text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{article.title}</h3>
                      <div className="flex gap-1.5 mt-1">
                        {article.tags?.slice(0, 3).map(tag => (
                          <Badge key={tag} variant="secondary" className="text-xs bg-indigo-50 text-indigo-600 border-none">{tag}</Badge>
                        ))}
                      </div>
                    </div>
                    <Badge variant={article.is_published ? "default" : "secondary"} className={`flex-shrink-0 ${article.is_published ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-500'}`}>
                      {article.is_published ? "Live" : "Entwurf"}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="flex-shrink-0" data-testid={`article-menu-${article.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/blog/${article.id}`)} data-testid={`edit-article-${article.id}`}>
                          <Pencil className="w-4 h-4 mr-2" /> Bearbeiten
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.open(`/blog/${article.id}`, '_blank')} data-testid={`view-article-${article.id}`}>
                          <Eye className="w-4 h-4 mr-2" /> Ansehen
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setDeleteDialog({ type: 'article', id: article.id, title: article.title })}
                          className="text-red-600 focus:text-red-600"
                          data-testid={`delete-article-${article.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" data-testid="settings-tab-content">
            <div className="max-w-2xl">
              <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-6">Einstellungen</h2>
              <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6">
                <div>
                  <Label htmlFor="siteName" className="text-slate-700 font-semibold">Seitenname</Label>
                  <Input
                    id="siteName"
                    value={settings.site_name || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, site_name: e.target.value }))}
                    className="mt-2 rounded-xl"
                    placeholder="Dein Markenname"
                    data-testid="settings-site-name"
                  />
                </div>
                <div>
                  <Label htmlFor="siteDesc" className="text-slate-700 font-semibold">Beschreibung</Label>
                  <Textarea
                    id="siteDesc"
                    value={settings.site_description || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, site_description: e.target.value }))}
                    className="mt-2 rounded-xl"
                    placeholder="Kurze Beschreibung deiner Seite"
                    rows={3}
                    data-testid="settings-site-description"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-semibold">Avatar / Logo</Label>
                  <div className="mt-2">
                    <ImageUploader
                      value={settings.avatar_url || ''}
                      onChange={(path) => setSettings(prev => ({ ...prev, avatar_url: path }))}
                    />
                  </div>
                </div>
                <Button onClick={handleSaveSettings} className="btn-gradient rounded-full px-8" data-testid="save-settings-btn">
                  Speichern
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>Löschen bestätigen</DialogTitle>
            <DialogDescription>
              Möchtest du "{deleteDialog?.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="rounded-full" data-testid="cancel-delete-btn">
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteDialog?.type === 'product' ? handleDeleteProduct(deleteDialog.id) : handleDeleteArticle(deleteDialog.id)}
              className="rounded-full"
              data-testid="confirm-delete-btn"
            >
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
